import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Container, Stepper, Step, StepLabel, Button, Box, Typography, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, useMediaQuery, useTheme } from '@mui/material';
import { toast } from 'sonner';
import { Toaster } from 'sonner';
import './index.css';
import OnboardingProfile from './OnboardingProfile';
import OnboardingUseCase from './OnboardingUseCase';
import OnboardingAITalker from './OnboardingAITalker';
import OnboardingPlayground from './OnboardingPlayground';
import OnboardingIntegration from './OnboardingIntegration';
import OnboardingPricing from './OnboardingPricing';
import OnboardingSummary from './OnboardingSummary';
import OnboardingComplete from './OnboardingComplete';
import { completeOnboarding, saveOnboardingState, loadOnboardingState, setUserProfile } from '../../redux/onboardingReducer';

const steps = ['Profile', 'Use Case', 'AI Talker Personality', 'Playground', 'Integration', 'Pick a Plan', 'Summary'];


const Onboarding = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { currentStep, profile, useCase, botConfig, testingResult, integrations, pricing, isProfileComplete } = useSelector(state => state.onboarding);
  const userIdFromRedux = useSelector(state => state.onboarding.userId);
  const userId = userIdFromRedux || localStorage.getItem('userId');
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [saveExitDialog, setSaveExitDialog] = useState(false);

  useEffect(() => {
    const loadState = async () => {
      await dispatch(loadOnboardingState());
      
      // Check if there's responseData in the URL
      const urlParams = new URLSearchParams(window.location.search);
      const responseData = urlParams.get('responseData');
      if (responseData) {
        try {
          const parsedData = JSON.parse(decodeURIComponent(responseData));
          if (parsedData.userId && parsedData.accessToken) {
            // Update the Redux store with the user data
            dispatch(setUserProfile({
              userId: parsedData.userId,
              // Add other relevant user data here
            }));
            // Store the access token and userId
            localStorage.setItem('accessToken', parsedData.accessToken);
            localStorage.setItem('userId', parsedData.userId);
            // Remove the responseData from the URL
            window.history.replaceState({}, document.title, "/onboarding");
          }
        } catch (error) {
          console.error("Error parsing responseData:", error);
        }
      }
    };

    loadState();
  }, [dispatch]);

  // Ensure progress is saved whenever it changes
  useEffect(() => {
    dispatch(saveOnboardingState());
  }, [currentStep, dispatch]);

  // Save progress to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('onboardingProgress', JSON.stringify({ currentStep }));
    dispatch(saveOnboardingState());
  }, [currentStep, dispatch]);

  // Use userId in your component logic
  useEffect(() => {
    if (userId) {
      // Perform actions that require userId
    } else {
      console.error('No user ID found');
      // Handle the case where no user ID is available
    }
  }, [userId]);

  const handleSaveAndExit = async () => {
    setLoading(true);
    try {
      await dispatch(saveOnboardingState());
      toast.success('Progress saved. You can resume later.');
    } catch (err) {
      toast.error('Failed to save progress');
    } finally {
      setLoading(false);
      setSaveExitDialog(false);
    }
  };

  const isStepComplete = (step) => {
    switch (step) {
      case 0:
        return isProfileComplete;
      case 1:
        return useCase !== '';
      case 2:
        return botConfig?.name && botConfig?.personality?.type && botConfig?.voice?.voice;
      case 3:
        return true
      case 4:
        return integrations?.length > 0;
      case 5:
        return pricing?.plan && pricing?.price;
      case 6:
        return true; // Summary step is always completable
      default:
        return false;
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return <OnboardingProfile />;
      case 1:
        return <OnboardingUseCase />;
      case 2:
        return <OnboardingAITalker />;
      case 3:
        return <OnboardingPlayground />;
      case 4:
        return <OnboardingIntegration />;
      case 5:
        return <OnboardingPricing />;
      case 6:
        return <OnboardingSummary />;
      default:
        return 'Unknown step';
    }
  };

  const handleComplete = () => {
    setOpenDialog(true);
  };

  const confirmCompletion = async () => {
    setLoading(true);
    try {
      await dispatch(saveOnboardingState());
      dispatch(completeOnboarding());
      setOpenDialog(false);
      setIsComplete(true);
      toast.success('Onboarding completed successfully');
      // Redirecting to dashboard
      navigate('/dashboard');
    } catch (err) {
      toast.error('Failed to complete onboarding');
    } finally {
      setLoading(false);
    }
  };


  if (isComplete) {
    return <OnboardingComplete />;
  }


  return (
    <>
      <Container maxWidth="lg" className="onboarding-container" sx={{ paddingTop: '20px' }}>
        <Box className="onboarding-stepper" sx={{ marginTop: '1px' }}>
          <Stepper activeStep={currentStep} alternativeLabel={!isMobile} orientation={isMobile ? "vertical" : "horizontal"}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{isMobile ? <Typography variant="body2">{label}</Typography> : label}</StepLabel>
              </Step>
            ))}
          </Stepper>
  
        </Box>
        <Box className="onboarding-content">
          {getStepContent(currentStep)}
        </Box>
      </Container>
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Complete Onboarding"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to complete the onboarding process? You can always make changes later in your settings.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={confirmCompletion} autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={saveExitDialog}
        onClose={() => setSaveExitDialog(false)}
        aria-labelledby="save-exit-dialog-title"
        aria-describedby="save-exit-dialog-description"
      >
        <DialogTitle id="save-exit-dialog-title">{"Save and Exit"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="save-exit-dialog-description">
            Are you sure you want to save your progress and exit? You can resume the onboarding process later.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveExitDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveAndExit} autoFocus>
            Save and Exit
          </Button>
        </DialogActions>
      </Dialog>
      <Toaster 
        position="top-right" 
        expand={false} 
        className='mt-24'
        richColors 
        toastOptions={{
          style: {
            marginTop: '60px', 
          },
        }}
      />
    </>
  );
};

export default Onboarding;
